import type { UiNode, UiNodeInputAttributes } from '@ory/client';

import { ButtonInput } from './Button';
import { CheckboxInput } from './Checkbox';
import { DefaultInput } from './Default';
import { EmailInput } from './Email';
import { HiddenInput } from './Hidden';
import { OidcSubmitInput } from './OidcSubmit';
import { OneTimePassword } from './OneTimePassword';
import { PasswordInput } from './Password';
import { ResendInput } from './Resend';
import { SendCodeSubmit } from './SendCodeSubmit';
import { SubmitInput } from './Submit';
import { TextInput } from './Text';

type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Inputノードディスパッチャーコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるinputノードを、適切なコンポーネントに振り分けます。
 *
 * 振り分けロジック:
 * - `type="hidden"` → HiddenInput
 * - `type="text"` → TextInput
 * - `type="email"` または `name="identifier"` または `name="email"` → EmailInput
 * - `type="password"` → PasswordInput
 * - `type="checkbox"` → CheckboxInput
 * - `type="button"` → ButtonInput
 * - `type="submit"` + `group="oidc"` → OidcSubmitInput
 * - `type="submit"` → SubmitInput
 * - その他（url, numberなど） → DefaultInput
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-input-nodes
 */
export function InputNode({ node, disabled }: Props) {
  // inputノードでない場合は何もレンダリングしない
  if (node.type !== 'input') {
    return null;
  }

  const attrs = node.attributes as UiNodeInputAttributes;

  // typeに基づいて適切なコンポーネントに振り分け
  switch (attrs.type) {
    case 'hidden':
      return <HiddenInput node={node} disabled={disabled} />;

    case 'text':
      // name属性でemail/identifierの場合はEmailInputを使用
      if (attrs.name === 'identifier' || attrs.name === 'email') {
        return <EmailInput node={node} disabled={disabled} />;
      }
      // name属性でtotp_code/codeの場合はOneTimePasswordを使用
      if (attrs.name === 'totp_code' || attrs.name === 'code') {
        return <OneTimePassword node={node} disabled={disabled} />;
      }

      return <TextInput node={node} disabled={disabled} />;

    case 'email':
      return <EmailInput node={node} disabled={disabled} />;

    case 'password':
      return <PasswordInput node={node} disabled={disabled} />;

    case 'checkbox':
      return <CheckboxInput node={node} disabled={disabled} />;

    case 'button':
      return <ButtonInput node={node} disabled={disabled} />;

    case 'submit': {
      const isResendNode =
        attrs.name === 'resend' ||
        attrs.name === 'resend_code' ||
        attrs.name === 'email';
      if (isResendNode) {
        return <ResendInput node={node} disabled={disabled} />;
      }
      if (node.group === 'oidc') {
        return <OidcSubmitInput node={node} disabled={disabled} />;
      }
      // 2FA で複数の送信先（メアド / 電話番号）に対しコードを送るケース。
      // group="code" + name="address" の submit を専用 UI で描画する。
      if (node.group === 'code' && attrs.name === 'address') {
        return <SendCodeSubmit node={node} disabled={disabled} />;
      }

      return <SubmitInput node={node} disabled={disabled} />;
    }

    default:
      // url, numberなど、その他のinputタイプ
      return <DefaultInput node={node} disabled={disabled} />;
  }
}
